/**
 * AssignStretchedClusterDialog
 *
 * Dialog for assigning a stretched cluster to one or more VMs.
 * A VM can only be assigned to the stretched cluster of the host it currently
 * runs on — no picklist needed, the domain is auto-determined.
 *
 * Sets `pf9_failure_domain` metadata key with value `primaryId:secondaryId`.
 *
 * @module AssignStretchedClusterDialog
 * @see AssignStretchedClusterDialog.spec.md
 */

import { makeStyles } from '@material-ui/styles'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import ShowActionState, { ActionStateResult } from 'core/components/ShowActionState'
import Button from 'core/elements/button'
import Modal from 'core/elements/modal'
import Text from 'core/elements/Text'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import { STRETCHED_CLUSTER_METADATA_KEY } from '../infrastructure/stretched-clusters/constants'
import { updateVmMetadata } from './actions'
import { IVirtualMachinesSelector } from './newModel'
import { stretchedClusterByHostIdSelector, stretchedClustersByClusterSelector } from './selectors'

const useStyles = makeStyles<Theme>((theme) => ({
  transitionBox: {
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.spacing(0.5),
    border: `1px solid ${theme.components.card.border}`,
    margin: theme.spacing(2, 0),
  },
  transitionBoxContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 3fr',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  chip: {
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 4,
    backgroundColor: theme.components.card.background,
    fontSize: 14,
  },
  arrow: {
    margin: theme.spacing(0, 2),
    fontSize: 18,
    color: theme.components.typography.passive,
  },
  infoText: {
    marginTop: 8,
  },
}))

interface Props {
  onClose: (success?: boolean) => void
  rows: IVirtualMachinesSelector[]
}

/**
 * Modal dialog that assigns a stretched cluster to VMs.
 * Domain is auto-determined from the VM's host — no user selection needed.
 */
export default function AssignStretchedClusterDialog({ onClose, rows }: Props) {
  const classes = useStyles()
  const stretchedClustersByCluster = useSelector(stretchedClustersByClusterSelector)
  const stretchedClusterByHostId = useSelector(stretchedClusterByHostIdSelector)
  const { update, bulkUpdate, updating, reset } = useUpdateAction(updateVmMetadata)
  const [bulkResults, setBulkResults] = useState<ActionStateResult[]>([])

  const isBulk = rows?.length > 1
  const firstVm = rows?.[0]

  const hostId = firstVm?.['OS-EXT-SRV-ATTR:host'] || ''
  const hostName = firstVm?.['OS-EXT-SRV-ATTR:hypervisor_hostname'] || hostId
  const hostDomain = stretchedClusterByHostId[hostId]

  const clusterName =
    firstVm?.pinned_availability_zone || firstVm?.['OS-EXT-AZ:availability_zone'] || ''
  const pair = stretchedClustersByCluster[clusterName]

  const peerDomain = useMemo(() => {
    if (!pair || !hostDomain) return null
    if (String(pair.domainA?.id) === String(hostDomain.id)) return pair.domainB
    if (String(pair.domainB?.id) === String(hostDomain.id)) return pair.domainA
    return null
  }, [pair, hostDomain])

  const buildMetadata = useCallback(
    (vm: IVirtualMachinesSelector) => {
      const vmHostId = vm?.['OS-EXT-SRV-ATTR:host'] || ''
      const vmHostDomain = stretchedClusterByHostId[vmHostId]
      const vmCluster = vm?.pinned_availability_zone || vm?.['OS-EXT-AZ:availability_zone'] || ''
      const vmPair = stretchedClustersByCluster[vmCluster]
      let primaryId = ''
      let secondaryId = ''
      if (vmHostDomain && vmPair) {
        primaryId = String(vmHostDomain.id)
        if (String(vmPair.domainA?.id) === primaryId) {
          secondaryId = String(vmPair.domainB?.id || '')
        } else {
          secondaryId = String(vmPair.domainA?.id || '')
        }
      }
      return {
        ...(vm?.metadata ?? {}),
        [STRETCHED_CLUSTER_METADATA_KEY]: `${primaryId}:${secondaryId}`,
      }
    },
    [stretchedClusterByHostId, stretchedClustersByCluster],
  )

  const handleClose = useCallback(
    (success?: boolean) => {
      reset()
      setBulkResults([])
      onClose(success)
    },
    [reset, onClose],
  )

  const handleSubmit = useCallback(async () => {
    if (isBulk) {
      const initialResults: ActionStateResult[] = rows.map((vm) => ({
        name: vm?.name ?? vm?.id,
        status: 'In Progress' as const,
        actionName: 'Assigning',
      }))
      setBulkResults(initialResults)

      const items = rows.map((vm) => ({
        id: vm.id,
        body: { metadata: buildMetadata(vm) },
      }))

      const results = await bulkUpdate(items)
      const finalResults: ActionStateResult[] = results.map((res, i) => ({
        name: rows[i]?.name ?? rows[i]?.id,
        status: (res?.success ? 'Success' : 'Failed') as ActionStateResult['status'],
        actionName: res?.success ? 'Assigned' : 'Failed to assign',
        error: res?.success ? undefined : res?.error?.message,
      }))
      setBulkResults(finalResults)

      if (results.every((r) => r?.success)) {
        handleClose(true)
      }
    } else {
      const body = { metadata: buildMetadata(firstVm) }
      const { success } = await update({ id: firstVm.id, body })
      if (success) {
        handleClose(true)
      }
    }
  }, [rows, firstVm, update, bulkUpdate, isBulk, buildMetadata, handleClose])

  const showFinishButton = bulkResults.some((r) => !!r?.status)
  // Per-VM domain info for transition rows
  const vmDomainInfo = useMemo(() => {
    return rows.map((vm) => {
      const vmHostId = vm?.['OS-EXT-SRV-ATTR:host'] || ''
      const vmDomain = stretchedClusterByHostId[vmHostId]
      return {
        vm,
        name: vm?.name ?? vm?.id,
        domainName: vmDomain?.name || null,
      }
    })
  }, [rows, stretchedClusterByHostId])

  const canAssign = vmDomainInfo.some((info) => !!info.domainName)

  return (
    <Modal
      panel="dialog"
      open
      onClose={() => handleClose()}
      title="Assign Stretched Cluster"
      maxWidth={600}
      footer={
        showFinishButton ? (
          <Button variant="secondary" onClick={() => handleClose(true)}>
            Finish
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={() => handleClose()}>
              Cancel
            </Button>
            <Button disabled={!canAssign || updating} onClick={handleSubmit} loading={updating}>
              Assign Stretched Cluster
            </Button>
          </>
        )
      }
    >
      {bulkResults.length > 0 ? (
        <ShowActionState
          title="Assigning stretched cluster to the following VMs:"
          results={bulkResults}
        />
      ) : (
        <>
          {!isBulk && (
            <Text variant="body2">
              <strong>{firstVm?.name ?? firstVm?.id}</strong> has no{' '}
              <strong>stretched cluster</strong> and runs on <strong>{hostName}</strong>
              {hostDomain ? (
                <>
                  , which is in <strong>{hostDomain.name}</strong>
                </>
              ) : null}
            </Text>
          )}
          {isBulk && (
            <Text variant="body2">
              The following VMs have no stretched cluster. Each will be assigned to the stretched
              cluster of the host it currently runs on.
            </Text>
          )}
          <div className={classes.transitionBox}>
            {vmDomainInfo.map((info) => (
              <div key={info.vm.id} className={classes.transitionBoxContainer}>
                <Text variant={'caption1'}>{info.name} :</Text>
                <Text variant={'body2'}>
                  no affinity
                  <span className={classes.arrow}>→</span>
                  {info.domainName || 'no domain'}
                </Text>
              </div>
            ))}
          </div>

          <Text variant="body2" className={classes.infoText}>
            A VM can only be added to the stretched cluster of the host it currently runs on.
            {!isBulk && hostDomain && (
              <>
                {' '}
                Consequently, <strong>{firstVm?.name ?? firstVm?.id}</strong> can be assigned to{' '}
                <strong>{hostDomain.name}</strong>.
                {peerDomain ? (
                  <>
                    {' '}
                    To assign it to <strong>{peerDomain.name}</strong>, migrate it to a host in that
                    stretched cluster first.
                  </>
                ) : null}
              </>
            )}
          </Text>
        </>
      )}
    </Modal>
  )
}
